import { OPTIONS_MULTIPLIER } from "@/lib/constants";
import type {
  DecayPoint,
  OptionLeg,
  PayoffPoint,
  PricedLeg,
  Structure,
} from "@/types/options";
import { callPrice, putPrice } from "./greeks";

// P&L at expiration for a single leg
function legPayoffAtExpiry(leg: OptionLeg, underlyingPrice: number): number {
  const intrinsic =
    leg.type === "call"
      ? Math.max(0, underlyingPrice - leg.strike)
      : Math.max(0, leg.strike - underlyingPrice);

  const direction = leg.side === "long" ? 1 : -1;
  return intrinsic * direction * leg.quantity;
}

// P&L at expiration for a set of legs, given entry credit/debit
export function payoffAtExpiry(
  legs: OptionLeg[],
  netCredit: number,
  underlyingPrice: number,
): number {
  const legPnl = legs.reduce(
    (sum, leg) => sum + legPayoffAtExpiry(leg, underlyingPrice),
    0,
  );
  return (legPnl + netCredit) * OPTIONS_MULTIPLIER;
}

// P&L now (with time value) for a set of priced legs (each carries its own T).
// sigmaForLeg, when provided, returns the per-strike implied vol used to price
// each leg via BS — letting the curve match real chain IV per strike rather
// than smoothing across the whole structure with a single flat sigma.
export function payoffWithTheta(
  legs: PricedLeg[],
  netCredit: number,
  underlyingPrice: number,
  r: number,
  sigma: number,
  sigmaForLeg?: (
    baseSigma: number,
    spot: number,
    strike: number,
    type: "put" | "call",
  ) => number,
): number {
  const currentValue = legs.reduce((sum, leg) => {
    const legSigma = sigmaForLeg
      ? sigmaForLeg(sigma, underlyingPrice, leg.strike, leg.type)
      : sigma;
    const params = {
      S: underlyingPrice,
      K: leg.strike,
      T: leg.T,
      r,
      sigma: legSigma,
    };
    const price = leg.type === "call" ? callPrice(params) : putPrice(params);
    const direction = leg.side === "long" ? 1 : -1;
    return sum + price * direction * leg.quantity;
  }, 0);
  return (currentValue + netCredit) * OPTIONS_MULTIPLIER;
}

// Type guard for PricedLeg
function isPricedLeg(leg: OptionLeg | PricedLeg): leg is PricedLeg {
  return "T" in leg && typeof leg.T === "number";
}

// Generate payoff curve data points
// When legs are PricedLeg[] and r/sigma provided, includes pnlWithTheta using per-leg T.
export function generatePayoffCurve(
  legs: (OptionLeg | PricedLeg)[],
  netCredit: number,
  center: number,
  range: number = 200,
  steps: number = 200,
  r?: number,
  sigma?: number,
  sigmaForLeg?: (
    baseSigma: number,
    spot: number,
    strike: number,
    type: "put" | "call",
  ) => number,
): PayoffPoint[] {
  const points: PayoffPoint[] = [];
  const minPrice = center - range;
  const maxPrice = center + range;
  const step = (maxPrice - minPrice) / steps;

  const hasPricing =
    r !== undefined &&
    sigma !== undefined &&
    sigma > 0 &&
    legs.length > 0 &&
    isPricedLeg(legs[0]);

  for (let i = 0; i <= steps; i++) {
    const price = minPrice + step * i;
    const pnl = payoffAtExpiry(legs, netCredit, price);
    const point: PayoffPoint = { price, pnl };

    if (hasPricing) {
      point.pnlWithTheta = payoffWithTheta(
        legs as PricedLeg[],
        netCredit,
        price,
        r,
        sigma,
        sigmaForLeg,
      );
    }

    points.push(point);
  }

  return points;
}

// ── Decay curve ────────────────────────────────────────────────────────────

/**
 * Generate a theta decay curve: P&L over time at a fixed spot price.
 * Sweeps T (annualized time-to-expiry) from startT down to endT.
 * Points are returned with decreasing T (left = more time, right = expiry).
 *
 * Each leg starts at its own initial T (from PricedLeg.T). As the global T
 * decreases, each leg's T decreases by the same elapsed amount:
 *   legT = leg.T - (startT - currentT)
 * This correctly handles calendar spreads where legs have different expirations.
 */
export function generateDecayCurve(
  legs: PricedLeg[],
  netCredit: number,
  spot: number,
  r: number,
  sigma: number,
  startT: number,
  endT: number,
  steps: number,
  sigmaForLeg?: (
    baseSigma: number,
    spot: number,
    strike: number,
    type: "put" | "call",
  ) => number,
): DecayPoint[] {
  const points: DecayPoint[] = [];
  if (startT <= endT || steps <= 0) return points;

  const range = startT - endT;
  for (let i = 0; i <= steps; i++) {
    const currentT = startT - (i / steps) * range;
    const elapsed = startT - currentT;

    const pnl = legs.reduce((sum, leg) => {
      const legT = Math.max(leg.T - elapsed, 1e-10);
      const legSigma = sigmaForLeg
        ? sigmaForLeg(sigma, spot, leg.strike, leg.type)
        : sigma;
      const params = { S: spot, K: leg.strike, T: legT, r, sigma: legSigma };
      const price = leg.type === "call" ? callPrice(params) : putPrice(params);
      const direction = leg.side === "long" ? 1 : -1;
      return sum + price * direction * leg.quantity;
    }, 0);

    points.push({ t: currentT, pnl: (pnl + netCredit) * OPTIONS_MULTIPLIER });
  }

  return points;
}

// Convert a Structure into OptionLeg arrays
export function structureToLegs(structure: Structure): {
  ironFlyLegs: OptionLeg[];
  upperBWBLegs: OptionLeg[];
  lowerBWBLegs: OptionLeg[];
  allLegs: OptionLeg[];
} {
  const { ironFly, upperBWB, lowerBWB } = structure;

  const ironFlyLegs: OptionLeg[] = [
    { strike: ironFly.shortCall, type: "call", side: "short", quantity: 1 },
    { strike: ironFly.shortPut, type: "put", side: "short", quantity: 1 },
    { strike: ironFly.longCall, type: "call", side: "long", quantity: 1 },
    { strike: ironFly.longPut, type: "put", side: "long", quantity: 1 },
  ];

  const upperBWBLegs: OptionLeg[] = [
    { strike: upperBWB.long1, type: "call", side: "long", quantity: 1 },
    { strike: upperBWB.short1, type: "call", side: "short", quantity: 1 },
    { strike: upperBWB.short2, type: "call", side: "short", quantity: 1 },
    { strike: upperBWB.long2, type: "call", side: "long", quantity: 1 },
  ];

  const lowerBWBLegs: OptionLeg[] = [
    { strike: lowerBWB.long1, type: "put", side: "long", quantity: 1 },
    { strike: lowerBWB.short1, type: "put", side: "short", quantity: 1 },
    { strike: lowerBWB.short2, type: "put", side: "short", quantity: 1 },
    { strike: lowerBWB.long2, type: "put", side: "long", quantity: 1 },
  ];

  return {
    ironFlyLegs,
    upperBWBLegs,
    lowerBWBLegs,
    allLegs: [...ironFlyLegs, ...upperBWBLegs, ...lowerBWBLegs],
  };
}

/**
 * Compute net call/put exposure to detect unbounded tails.
 * Net long calls → profit unbounded as price → ∞
 * Net short calls → loss unbounded as price → ∞
 * Puts are bounded (underlying can't go below 0).
 */
function tailExposure(legs: OptionLeg[]): { netCalls: number } {
  let netCalls = 0;
  for (const leg of legs) {
    if (leg.type === "call") {
      netCalls += (leg.side === "long" ? 1 : -1) * leg.quantity;
    }
  }
  return { netCalls };
}

/**
 * Calculate max profit, max loss, and breakevens at expiration.
 *
 * With legs, evaluate the exact piecewise-linear payoff at zero and every
 * strike, then solve each linear interval and the right tail. The sampled
 * points remain a fallback for callers that only have a curve.
 */
export function analyzePayoff(
  points: PayoffPoint[],
  legs?: OptionLeg[],
  netCredit?: number,
): {
  maxProfit: number;
  maxLoss: number;
  breakevens: number[];
} {
  if (!legs?.length) {
    let maxProfit = -Infinity;
    let maxLoss = Infinity;
    const breakevens: number[] = [];
    for (let index = 0; index < points.length; index += 1) {
      const current = points[index];
      maxProfit = Math.max(maxProfit, current.pnl);
      maxLoss = Math.min(maxLoss, current.pnl);
      if (index === 0) continue;
      const previous = points[index - 1];
      if (
        (previous.pnl < 0 && current.pnl >= 0) ||
        (previous.pnl >= 0 && current.pnl < 0)
      ) {
        const ratio =
          Math.abs(previous.pnl) /
          (Math.abs(previous.pnl) + Math.abs(current.pnl));
        breakevens.push(
          Math.round(
            (previous.price + ratio * (current.price - previous.price)) * 100,
          ) / 100,
        );
      }
    }
    return { maxProfit, maxLoss, breakevens };
  }

  const credit = netCredit ?? 0;
  const boundaries = [
    0,
    ...new Set(
      legs
        .map((leg) => leg.strike)
        .filter((strike) => Number.isFinite(strike) && strike > 0),
    ),
  ].sort((left, right) => left - right);
  const values = boundaries.map((price) => payoffAtExpiry(legs, credit, price));
  const breakevens: number[] = [];
  const addBreakeven = (value: number) => {
    if (!Number.isFinite(value) || value < 0) return;
    const rounded = Math.round(value * 100) / 100;
    if (breakevens.some((candidate) => Math.abs(candidate - rounded) < 0.005)) {
      return;
    }
    breakevens.push(rounded);
  };

  for (let index = 1; index < boundaries.length; index += 1) {
    const leftValue = values[index - 1];
    const rightValue = values[index];
    if (leftValue === 0 && rightValue === 0) continue;
    if (leftValue === 0) {
      addBreakeven(boundaries[index - 1]);
      continue;
    }
    if (rightValue === 0) {
      addBreakeven(boundaries[index]);
      continue;
    }
    if (Math.sign(leftValue) !== Math.sign(rightValue)) {
      addBreakeven(
        boundaries[index - 1] +
          (-leftValue * (boundaries[index] - boundaries[index - 1])) /
            (rightValue - leftValue),
      );
    }
  }

  const { netCalls } = tailExposure(legs);
  const tailStart = boundaries.at(-1) ?? 0;
  const tailValue = values.at(-1) ?? 0;
  const tailSlope = netCalls * OPTIONS_MULTIPLIER;
  if (tailValue === 0) addBreakeven(tailStart);
  else if (tailSlope !== 0) {
    const tailRoot = tailStart - tailValue / tailSlope;
    if (tailRoot > tailStart) addBreakeven(tailRoot);
  }

  return {
    maxProfit: tailSlope > 0 ? Infinity : Math.max(...values),
    maxLoss: tailSlope < 0 ? -Infinity : Math.min(...values),
    breakevens: breakevens.sort((left, right) => left - right),
  };
}
